// The buyer's attached files, shown on the pricing workspace whenever a
// request has any upload — regardless of whether lines exist yet. A
// salesperson reads the BOQ / PDF while typing prices.
//
// Ledger style: one section, horizontal rules, no cards. PDFs open in an
// inline <iframe> (same-origin, the session cookie rides along — no
// apiClient needed). Spreadsheets / other files download via a plain <a>.

import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext.jsx';
import styles from './AttachmentPanel.module.css';

function sizeLabel(bytes, t) {
  if (!bytes || bytes < 0) return t('workspace.attachment.sizeKb', { value: 0 });
  if (bytes >= 1024 * 1024) {
    return t('workspace.attachment.sizeMb', { value: (bytes / (1024 * 1024)).toFixed(1) });
  }
  return t('workspace.attachment.sizeKb', { value: Math.max(1, Math.round(bytes / 1024)) });
}

// `fileUrlBase` is the route prefix the per-file open/download links hang
// off — defaults to the sales pricing-workspace path so every existing
// caller is unchanged. The admin read-only request-detail screen passes
// `/admin/requests/:id/uploads` instead (same payload shape, no assignment
// check server-side).
export default function AttachmentPanel({ requestId, uploads, fileUrlBase }) {
  const { t } = useLanguage();
  const [openPdfId, setOpenPdfId] = useState(null);

  if (!uploads || uploads.length === 0) return null;

  const base = fileUrlBase || `/sales/requests/${requestId}/uploads`;

  return (
    <section className={styles.panel} aria-labelledby="attachment-panel-title">
      <h2 id="attachment-panel-title" className={styles.title}>
        {t('workspace.attachment.title')}
      </h2>

      <ul className={styles.list}>
        {uploads.map((upload) => {
          const fileUrl = `${base}/${upload.id}/file`;
          const isPdf = upload.kind === 'pdf';
          const isOpen = openPdfId === upload.id;

          return (
            <li key={upload.id} className={styles.row}>
              <div className={styles.rowMain}>
                <span className={styles.filename}>{upload.originalFilename}</span>
                <span className={styles.kind}>{t(`workspace.attachment.kind.${upload.kind}`)}</span>
                <span className={`${styles.size} tabularNums`}>
                  {sizeLabel(upload.sizeBytes, t)}
                </span>
                <span className={styles.action}>
                  {isPdf ? (
                    <button
                      type="button"
                      className={styles.linkButton}
                      onClick={() => setOpenPdfId(isOpen ? null : upload.id)}
                      aria-expanded={isOpen}
                    >
                      {isOpen ? t('workspace.attachment.hide') : t('workspace.attachment.view')}
                    </button>
                  ) : (
                    <a
                      className={styles.linkButton}
                      href={fileUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('workspace.attachment.download')}
                    </a>
                  )}
                </span>
              </div>

              {upload.parseStatus === 'failed' && (
                <p className={styles.callout}>{t('workspace.attachment.unreadable')}</p>
              )}
              {upload.needsMapping && upload.parseStatus === 'rows_extracted' && (
                <p className={styles.callout}>{t('workspace.attachment.needsMapping')}</p>
              )}

              {isPdf && isOpen && (
                <iframe className={styles.viewer} src={fileUrl} title={upload.originalFilename} />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
